import React, { useState, useEffect, useMemo } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiInfo, FiRefreshCw, FiDatabase, FiShield, FiXCircle, FiExternalLink } from 'react-icons/fi';
import { getProjects } from '../../api/projects';
import { getArchitecturesByProject } from '../../api/architectures';
import { getPurviewConnections, getPurviewStats, triggerPurviewScan, type PurviewStats, type PurviewConnection } from '../../api/purview';

// ─── Règles de conformité ────────────────────────────────────────────────────

interface ComplianceRule {
  id: string;
  name: string;
  description: string;
  category: 'Structure' | 'Nomenclature' | 'Gouvernance' | 'Sécurité';
  severity: 'error' | 'warning' | 'info';
  evaluate: (nodes: any[], edges: any[]) => { pass: boolean; violations: string[] };
}

const RULES: ComplianceRule[] = [
  {
    id: 'FAB-STRUCT-001',
    name: 'Workspace dans capacité',
    description: 'Chaque workspace Fabric doit être dans une capacité ou un domaine attaché à une capacité',
    category: 'Structure',
    severity: 'error',
    evaluate: (nodes) => {
      const capacityIds = new Set(nodes.filter(n => n.data?.type === 'fabric_capacity').map(n => n.id));
      const domainIds = new Set(nodes.filter(n => n.data?.type === 'fabric_domain' && capacityIds.has(n.parentNode || '')).map(n => n.id));
      const orphans = nodes.filter(n =>
        n.data?.type === 'fabric_workspace' &&
        !capacityIds.has(n.parentNode || '') &&
        !domainIds.has(n.parentNode || '')
      );
      return {
        pass: orphans.length === 0,
        violations: orphans.map(n => `Workspace "${n.data?.label || n.id}" sans capacité parente`),
      };
    },
  },
  {
    id: 'FAB-STRUCT-002',
    name: 'Items dans workspace',
    description: 'Les items Fabric (Lakehouse, Pipeline, Notebook…) doivent être dans un workspace',
    category: 'Structure',
    severity: 'error',
    evaluate: (nodes) => {
      const workspaceIds = new Set(nodes.filter(n => n.data?.type === 'fabric_workspace').map(n => n.id));
      const FABRIC_ITEMS = ['fabric_lakehouse','fabric_pipeline','fabric_notebook','fabric_dataset','fabric_dataflow',
        'fabric_report','fabric_semantic_model','fabric_warehouse','fabric_eventhouse','fabric_eventstream','fabric_ml_model'];
      const orphans = nodes.filter(n => FABRIC_ITEMS.includes(n.data?.type) && !workspaceIds.has(n.parentNode || ''));
      return {
        pass: orphans.length === 0,
        violations: orphans.map(n => `Item "${n.data?.label || n.data?.type || n.id}" hors workspace`),
      };
    },
  },
  {
    id: 'FAB-STRUCT-003',
    name: 'Capacité avec workspaces',
    description: 'Chaque capacité Fabric devrait avoir au moins un workspace',
    category: 'Structure',
    severity: 'warning',
    evaluate: (nodes) => {
      const capacities = nodes.filter(n => n.data?.type === 'fabric_capacity');
      const domainIds = new Set(nodes.filter(n => n.data?.type === 'fabric_domain').map(n => n.id));
      const empty = capacities.filter(cap => {
        const hasDirectWs = nodes.some(n => n.data?.type === 'fabric_workspace' && n.parentNode === cap.id);
        const domainsInCap = nodes.filter(n => n.data?.type === 'fabric_domain' && n.parentNode === cap.id).map(n => n.id);
        const hasDomainWs = nodes.some(n => n.data?.type === 'fabric_workspace' && domainsInCap.includes(n.parentNode || ''));
        return !hasDirectWs && !hasDomainWs;
      });
      return {
        pass: empty.length === 0,
        violations: empty.map(n => `Capacité "${n.data?.label || n.id}" sans workspace`),
      };
    },
  },
  {
    id: 'NAMING-001',
    name: 'Pas d\'espaces dans les noms',
    description: 'Les noms de ressources Fabric ne doivent pas contenir d\'espaces (risque Terraform)',
    category: 'Nomenclature',
    severity: 'warning',
    evaluate: (nodes) => {
      const FABRIC_TYPES = ['fabric_capacity','fabric_workspace','fabric_domain','fabric_lakehouse',
        'fabric_pipeline','fabric_notebook','fabric_warehouse'];
      const violating = nodes.filter(n => {
        if (!FABRIC_TYPES.includes(n.data?.type)) return false;
        const name = n.data?.params?.name || n.data?.params?.display_name || n.data?.label || '';
        return /\s/.test(name);
      });
      return {
        pass: violating.length === 0,
        violations: violating.map(n => `"${n.data?.label || n.id}" contient des espaces dans son nom`),
      };
    },
  },
  {
    id: 'NAMING-002',
    name: 'Noms suffisamment longs',
    description: 'Les noms de ressources doivent comporter au moins 3 caractères',
    category: 'Nomenclature',
    severity: 'warning',
    evaluate: (nodes) => {
      const violating = nodes.filter(n => {
        if (!n.data?.type?.startsWith('fabric_')) return false;
        const name = n.data?.params?.name || n.data?.params?.display_name || n.data?.label || '';
        return name.length > 0 && name.length < 3;
      });
      return {
        pass: violating.length === 0,
        violations: violating.map(n => `"${n.data?.label || n.id}" : nom trop court (${(n.data?.params?.name || n.data?.label || '').length} car.)`),
      };
    },
  },
  {
    id: 'GOV-001',
    name: 'Gouvernance des données',
    description: 'Une architecture avec 3+ workspaces devrait inclure Microsoft Purview',
    category: 'Gouvernance',
    severity: 'info',
    evaluate: (nodes) => {
      const wsCount = nodes.filter(n => n.data?.type === 'fabric_workspace').length;
      const hasPurview = nodes.some(n => n.data?.type === 'azurerm_purview_account' || n.data?.type === 'purview_account');
      if (wsCount < 3) return { pass: true, violations: [] };
      return {
        pass: hasPurview,
        violations: hasPurview ? [] : [`${wsCount} workspaces sans Purview configuré dans l'architecture`],
      };
    },
  },
  {
    id: 'GOV-002',
    name: 'Items connectés',
    description: 'Les items Fabric devraient être connectés à au moins un autre nœud',
    category: 'Gouvernance',
    severity: 'info',
    evaluate: (nodes, edges) => {
      const FABRIC_ITEMS = ['fabric_lakehouse','fabric_pipeline','fabric_notebook','fabric_semantic_model','fabric_report'];
      const connectedIds = new Set([...edges.map(e => e.source), ...edges.map(e => e.target)]);
      const isolated = nodes.filter(n => FABRIC_ITEMS.includes(n.data?.type) && !connectedIds.has(n.id));
      return {
        pass: isolated.length === 0,
        violations: isolated.map(n => `"${n.data?.label || n.id}" (${n.data?.type}) : aucune connexion`),
      };
    },
  },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface ArchCompliance {
  archId: string;
  archName: string;
  projectName: string;
  score: number;
  passed: number;
  total: number;
  findings: Array<{ ruleId: string; ruleName: string; severity: 'error' | 'warning' | 'info'; violations: string[] }>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SEV_ORDER = { error: 0, warning: 1, info: 2 };

function scoreColor(score: number) {
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-amber-600';
  return 'text-red-600';
}

function scoreBg(score: number) {
  if (score >= 90) return 'bg-green-50 border-green-200';
  if (score >= 70) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
}

const SeverityIcon: React.FC<{ sev: 'error' | 'warning' | 'info' }> = ({ sev }) =>
  sev === 'error'   ? <FiXCircle size={14} className="text-red-500 flex-shrink-0" /> :
  sev === 'warning' ? <FiAlertTriangle size={14} className="text-amber-500 flex-shrink-0" /> :
                      <FiInfo size={14} className="text-blue-500 flex-shrink-0" />;

// ─── Purview Panel ────────────────────────────────────────────────────────────

const PurviewPanel: React.FC = () => {
  const [connections, setConnections] = useState<PurviewConnection[]>([]);
  const [selectedConn, setSelectedConn] = useState<string>('');
  const [stats, setStats] = useState<PurviewStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanningDs, setScanningDs] = useState<string | null>(null);
  const [scanMsg, setScanMsg] = useState<{ ds: string; msg: string; ok: boolean } | null>(null);

  useEffect(() => {
    getPurviewConnections()
      .then(conns => {
        const active = conns.filter(c => c.is_active);
        setConnections(active);
        if (active.length > 0) setSelectedConn(active[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedConn) return;
    setLoading(true);
    setError(null);
    setStats(null);
    getPurviewStats(selectedConn)
      .then(setStats)
      .catch(err => setError(err?.response?.data?.error || 'Erreur de chargement Purview'))
      .finally(() => setLoading(false));
  }, [selectedConn]);

  const handleScan = async (dsName: string) => {
    if (!selectedConn) return;
    setScanningDs(dsName);
    setScanMsg(null);
    try {
      const result = await triggerPurviewScan(dsName, selectedConn);
      setScanMsg({ ds: dsName, msg: result.message, ok: true });
    } catch (err: any) {
      setScanMsg({ ds: dsName, msg: err?.response?.data?.error || 'Erreur scan', ok: false });
    } finally {
      setScanningDs(null);
    }
  };

  if (connections.length === 0) {
    return (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
        <span style={{ fontSize: 40 }}>🔍</span>
        <p className="font-medium text-purple-800 mt-3 mb-1">Microsoft Purview non configuré</p>
        <p className="text-sm text-purple-600">
          Ajoutez une connexion Purview dans <strong>Paramètres → Connexions</strong> pour visualiser les assets classifiés et déclencher des scans.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span style={{ fontSize: 20 }}>🔍</span>
          <h3 className="font-semibold text-gray-900">Microsoft Purview</h3>
        </div>
        {connections.length > 1 && (
          <select value={selectedConn} onChange={e => setSelectedConn(e.target.value)}
            className="border border-gray-200 rounded px-2 py-1 text-sm">
            {connections.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>

      <div className="p-6">
        {loading && (
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <FiRefreshCw size={14} className="animate-spin" /> Chargement Purview…
          </div>
        )}
        {error && <p className="text-red-500 text-sm">{error}</p>}
        {stats && (
          <>
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { value: stats.totalAssets.toLocaleString('fr-FR'), label: 'Assets catalogués', gradient: 'linear-gradient(135deg, #5B21B6 0%, #8B5CF6 100%)', shadow: 'rgba(139,92,246,0.35)' },
                { value: stats.classifications.length, label: 'Classifications', gradient: 'linear-gradient(135deg, #4338CA 0%, #818CF8 100%)', shadow: 'rgba(99,102,241,0.35)' },
                { value: stats.dataSourceCount, label: 'Sources de données', gradient: 'linear-gradient(135deg, #6D28D9 0%, #A78BFA 100%)', shadow: 'rgba(167,139,250,0.35)' },
              ].map(({ value, label, gradient, shadow }) => (
                <div key={label} style={{ background: gradient, borderRadius: '0.875rem', padding: '1.25rem', color: 'white', boxShadow: `0 4px 16px ${shadow}`, position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
                  <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                  <p style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>{value}</p>
                  <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', marginTop: '0.4rem' }}>{label}</p>
                </div>
              ))}
            </div>

            {stats.classifications.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Top classifications</h4>
                <div className="space-y-2">
                  {stats.classifications.slice(0, 6).map(cl => {
                    const pct = stats.totalAssets > 0 ? Math.round((cl.count / stats.totalAssets) * 100) : 0;
                    return (
                      <div key={cl.name} className="flex items-center gap-3">
                        <span className="text-xs text-gray-600 w-48 truncate" title={cl.name}>{cl.name}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                          <div className="h-1.5 bg-purple-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 w-14 text-right">{cl.count.toLocaleString('fr-FR')}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {stats.dataSources.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Sources de données enregistrées</h4>
                <div className="space-y-2">
                  {stats.dataSources.map(ds => (
                    <div key={ds.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FiDatabase size={14} className="text-purple-500" />
                        <span className="text-sm text-gray-800">{ds.name}</span>
                        <span className="text-xs text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">{ds.kind}</span>
                      </div>
                      <button
                        onClick={() => handleScan(ds.name)}
                        disabled={scanningDs === ds.name}
                        className="text-xs px-3 py-1 bg-purple-100 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-200 disabled:opacity-50 transition"
                      >
                        {scanningDs === ds.name ? '…' : 'Scanner'}
                      </button>
                    </div>
                  ))}
                  {scanMsg && (
                    <p className={`text-xs mt-2 ${scanMsg.ok ? 'text-green-600' : 'text-red-500'}`}>
                      {scanMsg.msg}
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main Tab ─────────────────────────────────────────────────────────────────

const ComplianceTab: React.FC = () => {
  const [architectures, setArchitectures] = useState<ArchCompliance[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedArch, setExpandedArch] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const projects = await getProjects() as any[];
        const results: ArchCompliance[] = [];

        for (const proj of projects) {
          try {
            const archs = await getArchitecturesByProject(proj.id);
            for (const arch of archs) {
              const nodes: any[] = (arch.diagram_data as any)?.nodes || [];
              const edges: any[] = (arch.diagram_data as any)?.edges || [];

              // Only evaluate architectures with Fabric content
              const hasFabric = nodes.some(n => n.data?.type?.startsWith('fabric_'));
              if (!hasFabric) continue;

              const findings: ArchCompliance['findings'] = [];
              for (const rule of RULES) {
                const { pass, violations } = rule.evaluate(nodes, edges);
                if (!pass) {
                  findings.push({ ruleId: rule.id, ruleName: rule.name, severity: rule.severity, violations });
                }
              }

              findings.sort((a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity]);

              const errors = findings.filter(f => f.severity === 'error').length;
              const passed = RULES.length - findings.length;
              const score = Math.round((passed / RULES.length) * 100);

              results.push({
                archId: arch.id,
                archName: arch.name,
                projectName: proj.name,
                score,
                passed,
                total: RULES.length,
                findings,
              });
            }
          } catch { /* non bloquant */ }
        }

        results.sort((a, b) => a.score - b.score);
        setArchitectures(results);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const globalScore = useMemo(() => {
    if (architectures.length === 0) return null;
    return Math.round(architectures.reduce((s, a) => s + a.score, 0) / architectures.length);
  }, [architectures]);

  const totalErrors = useMemo(() =>
    architectures.reduce((s, a) => s + a.findings.filter(f => f.severity === 'error').length, 0),
    [architectures]
  );
  const totalWarnings = useMemo(() =>
    architectures.reduce((s, a) => s + a.findings.filter(f => f.severity === 'warning').length, 0),
    [architectures]
  );

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <FiRefreshCw size={24} className="animate-spin text-purple-500 mr-3" />
      <span className="text-gray-500">Analyse de conformité en cours…</span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
        {/* Score global */}
        {(() => {
          const g = globalScore !== null
            ? globalScore >= 90 ? 'linear-gradient(135deg, #065F46 0%, #10B981 100%)'
              : globalScore >= 70 ? 'linear-gradient(135deg, #92400E 0%, #F59E0B 100%)'
              : 'linear-gradient(135deg, #991B1B 0%, #EF4444 100%)'
            : 'linear-gradient(135deg, #374151 0%, #9CA3AF 100%)';
          const s = globalScore !== null
            ? globalScore >= 90 ? 'rgba(16,185,129,0.35)' : globalScore >= 70 ? 'rgba(245,158,11,0.35)' : 'rgba(239,68,68,0.35)'
            : 'rgba(156,163,175,0.35)';
          return (
            <div style={{ background: g, borderRadius: '0.875rem', padding: '1.25rem', color: 'white', boxShadow: `0 4px 16px ${s}`, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
              <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '0.4rem' }}>Score global</p>
              <p style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, marginBottom: '0.25rem' }}>{globalScore !== null ? `${globalScore}%` : 'N/D'}</p>
              <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.65)' }}>{architectures.length} architecture{architectures.length > 1 ? 's' : ''} évaluée{architectures.length > 1 ? 's' : ''}</p>
            </div>
          );
        })()}
        {/* Erreurs critiques */}
        <div style={{ background: totalErrors > 0 ? 'linear-gradient(135deg, #991B1B 0%, #EF4444 100%)' : 'linear-gradient(135deg, #065F46 0%, #10B981 100%)', borderRadius: '0.875rem', padding: '1.25rem', color: 'white', boxShadow: `0 4px 16px ${totalErrors > 0 ? 'rgba(239,68,68,0.35)' : 'rgba(16,185,129,0.35)'}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '0.4rem' }}>Erreurs critiques</p>
          <p style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, marginBottom: '0.25rem' }}>{totalErrors}</p>
          <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.65)' }}>Violations bloquantes</p>
        </div>
        {/* Avertissements */}
        <div style={{ background: totalWarnings > 0 ? 'linear-gradient(135deg, #92400E 0%, #F59E0B 100%)' : 'linear-gradient(135deg, #065F46 0%, #10B981 100%)', borderRadius: '0.875rem', padding: '1.25rem', color: 'white', boxShadow: `0 4px 16px ${totalWarnings > 0 ? 'rgba(245,158,11,0.35)' : 'rgba(16,185,129,0.35)'}`, position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '0.4rem' }}>Avertissements</p>
          <p style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, marginBottom: '0.25rem' }}>{totalWarnings}</p>
          <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.65)' }}>À corriger</p>
        </div>
        {/* Règles actives */}
        <div style={{ background: 'linear-gradient(135deg, #4338CA 0%, #818CF8 100%)', borderRadius: '0.875rem', padding: '1.25rem', color: 'white', boxShadow: '0 4px 16px rgba(99,102,241,0.35)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -16, right: -16, width: 64, height: 64, background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
          <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: '0.4rem' }}>Règles actives</p>
          <p style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, marginBottom: '0.25rem' }}>{RULES.length}</p>
          <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.65)' }}>Structure · Nomenclature · Gouvernance</p>
        </div>
      </div>

      {/* Architectures */}
      {architectures.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FiShield size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium mb-2">Aucune architecture à évaluer</p>
          <p className="text-gray-400 text-sm">Créez des architectures Fabric dans vos projets pour voir l'analyse de conformité.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Conformité par architecture</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {architectures.map(arch => (
              <div key={arch.archId}>
                <div
                  className="px-6 py-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition"
                  onClick={() => setExpandedArch(expandedArch === arch.archId ? null : arch.archId)}
                >
                  {/* Score ring */}
                  <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${scoreBg(arch.score)}`}>
                    <span className={`text-sm font-bold ${scoreColor(arch.score)}`}>{arch.score}%</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{arch.archName}</span>
                      <span className="text-xs text-gray-400">— {arch.projectName}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-40">
                        <div
                          className={`h-1.5 rounded-full ${arch.score >= 90 ? 'bg-green-500' : arch.score >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${arch.score}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{arch.passed}/{arch.total} règles</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {arch.findings.filter(f => f.severity === 'error').length > 0 && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                        <FiXCircle size={11} />
                        {arch.findings.filter(f => f.severity === 'error').length}
                      </span>
                    )}
                    {arch.findings.filter(f => f.severity === 'warning').length > 0 && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                        <FiAlertTriangle size={11} />
                        {arch.findings.filter(f => f.severity === 'warning').length}
                      </span>
                    )}
                    {arch.findings.length === 0 && (
                      <FiCheckCircle size={18} className="text-green-500" />
                    )}
                  </div>
                </div>

                {expandedArch === arch.archId && arch.findings.length > 0 && (
                  <div className="px-6 pb-4 ml-16 space-y-3">
                    {arch.findings.map(f => (
                      <div key={f.ruleId} className={`rounded-lg p-3 border text-sm ${
                        f.severity === 'error' ? 'bg-red-50 border-red-200' :
                        f.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
                        'bg-blue-50 border-blue-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          <SeverityIcon sev={f.severity} />
                          <span className="font-medium text-gray-800">{f.ruleName}</span>
                          <span className="text-xs text-gray-400 ml-auto font-mono">{f.ruleId}</span>
                        </div>
                        <ul className="space-y-0.5">
                          {f.violations.map((v, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                              <span className="mt-0.5">·</span>{v}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    {arch.findings.length === 0 && (
                      <p className="text-sm text-green-600 flex items-center gap-2">
                        <FiCheckCircle size={14} /> Toutes les règles sont respectées
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Référentiel de règles */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Référentiel de politiques</h3>
        </div>
        <div className="divide-y divide-gray-50">
          {RULES.map(rule => (
            <div key={rule.id} className="px-6 py-3 flex items-center gap-4">
              <span className="font-mono text-xs text-gray-400 w-28 flex-shrink-0">{rule.id}</span>
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-800">{rule.name}</span>
                <p className="text-xs text-gray-500">{rule.description}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                rule.category === 'Structure' ? 'bg-blue-100 text-blue-700' :
                rule.category === 'Nomenclature' ? 'bg-orange-100 text-orange-700' :
                rule.category === 'Gouvernance' ? 'bg-purple-100 text-purple-700' :
                'bg-gray-100 text-gray-600'
              }`}>{rule.category}</span>
              <SeverityIcon sev={rule.severity} />
            </div>
          ))}
        </div>
      </div>

      {/* Purview */}
      <PurviewPanel />
    </div>
  );
};

export default ComplianceTab;
